/**
 * FORTRESS CONTEXT - Centralized Backend-Driven Access Control
 * ============================================================
 * 
 * This context is the SINGLE SOURCE OF TRUTH for:
 * - Feature kill-switches (global enable/disable)
 * - User monetization status (PRO, tokens, daily limits)
 * - Access validation (server-side gating)
 * - Session tracking
 * - Breadcrumb tracking
 * 
 * CRITICAL RULE: Frontend NEVER independently decides entitlements.
 * All access decisions come from the backend via /api/fortress/* endpoints.
 * 
 * Usage:
 * 1. Wrap app with <FortressProvider>
 * 2. Use useFortress() hook to access state
 * 3. Call validateAccess(feature) before gated actions
 * 4. Check isFeatureEnabled(key) for kill-switches
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Feature keys (must match backend fortress/kill_switch.py)
export const FEATURE_KEYS = {
  USTADZ_TAJWEED: 'ustadz_tajweed_enabled',
  MOSQUE_SYNC: 'mosque_sync_enabled',
  KIDS_PARADISE: 'kids_paradise_enabled',
  AD_UNLOCK: 'ad_unlock_enabled',
  REVENUECAT_PRO: 'revenuecat_pro_enabled',
  TOKEN_SYSTEM: 'token_system_enabled',
  AI_FEATURES: 'ai_features_enabled',
  GEMINI_TTS: 'gemini_tts_enabled',
  WHISPER_STT: 'whisper_stt_enabled',
  LIVE_MENTOR: 'live_mentor_enabled',
  VOICE_CHAT: 'voice_chat_enabled',
  MAINTENANCE_MODE: 'maintenance_mode',
  NEW_USER_REGISTRATION: 'new_user_registration_enabled',
};

// Storage keys
const STORAGE_KEYS = {
  USER_ID: 'fortress_user_id',
  SESSION_ID: 'fortress_session_id',
  CONFIG_CACHE: 'fortress_config_cache',
  CONFIG_TIMESTAMP: 'fortress_config_timestamp',
};

// Config cache TTL (30 seconds - matches backend)
const CONFIG_CACHE_TTL_MS = 30 * 1000;

// Generate unique IDs
const generateId = (prefix) => `${prefix}_${Math.random().toString(36).substring(2, 15)}_${Date.now()}`;

// Get or create user ID (persisted)
const getOrCreateUserId = () => {
  let userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  if (!userId) {
    userId = generateId('user');
    localStorage.setItem(STORAGE_KEYS.USER_ID, userId);
  }
  return userId;
};

// Get or create session ID (per browser session)
const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!sessionId) {
    sessionId = generateId('sess');
    sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
  }
  return sessionId;
};

// Context
const FortressContext = createContext(null);

/**
 * FortressProvider - Wraps app with backend-driven access control
 */
export const FortressProvider = ({ children }) => {
  // IDs
  const [userId] = useState(getOrCreateUserId);
  const [sessionId] = useState(getOrCreateSessionId);
  
  // Feature config (kill-switches)
  const [featureConfig, setFeatureConfig] = useState({});
  const [configLoaded, setConfigLoaded] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  
  // User monetization status
  const [monetizationStatus, setMonetizationStatus] = useState({
    isPro: false,
    proExpiresAt: null,
    tokenBalance: 0,
    dailyUsage: {},
    dailyLimits: {},
    canWatchAd: true,
    nextAdAvailableAt: null,
  });
  const [monetizationLoaded, setMonetizationLoaded] = useState(false);
  
  // System health
  const [systemHealthy, setSystemHealthy] = useState(true);
  
  // Loading states
  const [isValidating, setIsValidating] = useState(false);
  
  // Breadcrumb tracking
  const lastTrackedRoute = useRef(null);
  
  // ============================================================
  // FEATURE CONFIG (Kill-Switches)
  // ============================================================
  
  /**
   * Fetch feature config from backend
   */
  const fetchFeatureConfig = useCallback(async (force = false) => {
    // Check cache first (unless forced)
    if (!force) {
      try {
        const cached = localStorage.getItem(STORAGE_KEYS.CONFIG_CACHE);
        const timestamp = localStorage.getItem(STORAGE_KEYS.CONFIG_TIMESTAMP);
        
        if (cached && timestamp) {
          const age = Date.now() - parseInt(timestamp, 10);
          if (age < CONFIG_CACHE_TTL_MS) {
            const config = JSON.parse(cached);
            setFeatureConfig(config.config || {});
            setMaintenanceMode(config.maintenance_mode || false);
            setConfigLoaded(true);
            return config;
          }
        }
      } catch (e) {
        console.warn('[Fortress] Cache read failed:', e);
      }
    }
    
    try {
      const response = await fetch(`${API}/api/fortress/config/all`);
      
      if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Update state
      setFeatureConfig(data.config || {});
      setMaintenanceMode(data.maintenance_mode || false);
      setConfigLoaded(true);
      setSystemHealthy(true);
      
      // Cache
      localStorage.setItem(STORAGE_KEYS.CONFIG_CACHE, JSON.stringify(data));
      localStorage.setItem(STORAGE_KEYS.CONFIG_TIMESTAMP, Date.now().toString());
      
      return data;
    } catch (error) {
      console.error('[Fortress] Failed to fetch config:', error);
      setSystemHealthy(false);
      
      // Fall back to cached config if available
      try {
        const cached = localStorage.getItem(STORAGE_KEYS.CONFIG_CACHE);
        if (cached) {
          const config = JSON.parse(cached);
          setFeatureConfig(config.config || {});
          setMaintenanceMode(config.maintenance_mode || false);
          setConfigLoaded(true);
        }
      } catch (e) {
        // No cache available
      }
      
      return null;
    }
  }, []);
  
  /**
   * Check if a feature is enabled (kill-switch)
   */
  const isFeatureEnabled = useCallback((featureKey) => {
    // In maintenance mode, only system features work
    if (maintenanceMode && featureKey !== FEATURE_KEYS.MAINTENANCE_MODE) {
      return false;
    }
    
    // Default to enabled if unknown (fail-open for better UX, backend enforces)
    return featureConfig[featureKey] !== false;
  }, [featureConfig, maintenanceMode]);
  
  // ============================================================
  // MONETIZATION STATUS
  // ============================================================
  
  /**
   * Fetch user monetization status from backend
   */
  const fetchMonetizationStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/fortress/monetization/status/${userId}`);
      
      if (!response.ok) {
        throw new Error(`Monetization fetch failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      setMonetizationStatus({
        isPro: data.is_pro || false,
        proExpiresAt: data.pro_expires_at,
        tokenBalance: data.token_balance || 0,
        dailyUsage: data.daily_usage || {},
        dailyLimits: data.daily_limits || {},
        canWatchAd: data.can_watch_ad !== false,
        nextAdAvailableAt: data.next_ad_available_at,
      });
      setMonetizationLoaded(true);
      
      return data;
    } catch (error) {
      console.error('[Fortress] Failed to fetch monetization:', error);
      setMonetizationLoaded(true); // Mark as loaded to prevent infinite loading
      return null;
    }
  }, [userId]);
  
  /**
   * Refresh monetization status
   */
  const refreshMonetization = useCallback(async () => {
    return fetchMonetizationStatus();
  }, [fetchMonetizationStatus]);
  
  // ============================================================
  // ACCESS VALIDATION (Main Gating)
  // ============================================================
  
  /**
   * Validate access to a feature
   * This is the MAIN GATING FUNCTION - call before any gated action
   * 
   * @param {string} feature - Feature identifier (e.g., 'tajweed_coach')
   * @returns {Promise<{allowed: boolean, reason: string, ...}>}
   */
  const validateAccess = useCallback(async (feature) => {
    setIsValidating(true);
    
    try {
      const response = await fetch(
        `${API}/api/fortress/access-check?user_id=${userId}&session_id=${sessionId}&feature=${feature}`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        throw new Error(`Access check failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Update monetization state from response
      if (result.remaining_balance !== undefined) {
        setMonetizationStatus(prev => ({
          ...prev,
          tokenBalance: result.remaining_balance,
        }));
      }
      
      return {
        allowed: result.allowed,
        reason: result.reason,
        tokenCost: result.token_cost || 0,
        remainingBalance: result.remaining_balance,
        remainingDaily: result.remaining_daily,
        requirePayment: result.require_payment || false,
        canUseAd: result.can_use_ad || false,
        breadcrumbTrailId: result.breadcrumb_trail_id,
        suspicious: result.suspicious || false,
      };
    } catch (error) {
      console.error('[Fortress] Access validation failed:', error);
      
      // Fail-open for better UX (backend enforces anyway)
      return {
        allowed: true,
        reason: 'Offline mode - access granted',
        tokenCost: 0,
        remainingBalance: monetizationStatus.tokenBalance,
        remainingDaily: null,
        requirePayment: false,
        canUseAd: true,
        breadcrumbTrailId: null,
        suspicious: false,
        offline: true,
      };
    } finally {
      setIsValidating(false);
    }
  }, [userId, sessionId, monetizationStatus.tokenBalance]);
  
  /**
   * Consume tokens for a feature (after successful usage)
   */
  const consumeTokens = useCallback(async (feature, amount = null) => {
    try {
      const response = await fetch(
        `${API}/api/fortress/monetization/consume?user_id=${userId}&feature=${feature}${amount ? `&amount=${amount}` : ''}&session_id=${sessionId}`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        throw new Error(`Token consumption failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setMonetizationStatus(prev => ({
          ...prev,
          tokenBalance: result.remaining_balance,
        }));
      }
      
      return result;
    } catch (error) {
      console.error('[Fortress] Token consumption failed:', error);
      return { success: false, error: error.message };
    }
  }, [userId, sessionId]);
  
  /**
   * Process ad reward
   */
  const processAdReward = useCallback(async (adType = 'rewarded_video', adId = null) => {
    try {
      const response = await fetch(
        `${API}/api/fortress/monetization/ad-reward?user_id=${userId}&ad_type=${adType}${adId ? `&ad_id=${adId}` : ''}`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        throw new Error(`Ad reward failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        // Refresh monetization to get updated balance
        await refreshMonetization();
      }
      
      return result;
    } catch (error) {
      console.error('[Fortress] Ad reward failed:', error);
      return { success: false, error: error.message };
    }
  }, [userId, refreshMonetization]);
  
  // ============================================================
  // SESSION TRACKING
  // ============================================================
  
  /**
   * Start a gated session
   */
  const startSession = useCallback(async (sessionType, unlockSource, options = {}) => {
    try {
      const response = await fetch(`${API}/api/fortress/sessions/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          session_type: sessionType,
          unlock_source: unlockSource,
          feature: options.feature,
          token_cost: options.tokenCost || 0,
          ai_cost_category: options.aiCostCategory,
          metadata: options.metadata,
          breadcrumb_trail_id: options.breadcrumbTrailId,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Session start failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('[Fortress] Session start failed:', error);
      return { error: error.message };
    }
  }, [userId]);
  
  /**
   * End a gated session
   */
  const endSession = useCallback(async (sessionId, completed = true, error = null) => {
    try {
      const response = await fetch(`${API}/api/fortress/sessions/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          completed,
          error,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Session end failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('[Fortress] Session end failed:', error);
      return { error: error.message };
    }
  }, []);
  
  // ============================================================
  // BREADCRUMB TRACKING
  // ============================================================
  
  /**
   * Track navigation breadcrumb
   * Call this on route changes
   */
  const trackBreadcrumb = useCallback(async (route, referrer = null, metadata = null) => {
    // Dedupe same route
    if (lastTrackedRoute.current === route) return;
    lastTrackedRoute.current = route;
    
    try {
      await fetch(
        `${API}/api/fortress/track?user_id=${userId}&session_id=${sessionId}&route=${encodeURIComponent(route)}${referrer ? `&referrer=${encodeURIComponent(referrer)}` : ''}`,
        { 
          method: 'POST',
          headers: metadata ? { 'Content-Type': 'application/json' } : undefined,
          body: metadata ? JSON.stringify({ metadata }) : undefined,
        }
      );
    } catch (error) {
      // Silent fail - breadcrumbs are non-critical
      console.debug('[Fortress] Breadcrumb tracking failed:', error);
    }
  }, [userId, sessionId]);
  
  // ============================================================
  // HEALTH CHECK
  // ============================================================
  
  /**
   * Check system health
   */
  const checkHealth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/fortress/health`);
      const data = await response.json();
      
      setSystemHealthy(data.status === 'operational');
      setMaintenanceMode(data.maintenance_mode || false);
      
      return data;
    } catch (error) {
      console.error('[Fortress] Health check failed:', error);
      setSystemHealthy(false);
      return { status: 'error', error: error.message };
    }
  }, []);
  
  // ============================================================
  // INITIALIZATION
  // ============================================================
  
  useEffect(() => {
    // Initial load
    fetchFeatureConfig();
    fetchMonetizationStatus();
    
    // Periodic refresh (every 30 seconds)
    const configInterval = setInterval(() => {
      fetchFeatureConfig();
    }, CONFIG_CACHE_TTL_MS);
    
    // Less frequent monetization refresh (every 2 minutes)
    const monetizationInterval = setInterval(() => {
      fetchMonetizationStatus();
    }, 2 * 60 * 1000);
    
    return () => {
      clearInterval(configInterval);
      clearInterval(monetizationInterval);
    };
  }, [fetchFeatureConfig, fetchMonetizationStatus]);
  
  // ============================================================
  // CONTEXT VALUE
  // ============================================================
  
  const value = {
    // IDs
    userId,
    sessionId,
    
    // Feature Config (Kill-Switches)
    featureConfig,
    configLoaded,
    maintenanceMode,
    isFeatureEnabled,
    fetchFeatureConfig,
    
    // Monetization
    monetizationStatus,
    monetizationLoaded,
    isPro: monetizationStatus.isPro,
    tokenBalance: monetizationStatus.tokenBalance,
    canWatchAd: monetizationStatus.canWatchAd,
    refreshMonetization,
    
    // Access Validation
    validateAccess,
    isValidating,
    consumeTokens,
    processAdReward,
    
    // Session Tracking
    startSession,
    endSession,
    
    // Breadcrumb Tracking
    trackBreadcrumb,
    
    // System Health
    systemHealthy,
    checkHealth,
    
    // Loading state
    isLoaded: configLoaded && monetizationLoaded,
  };
  
  return (
    <FortressContext.Provider value={value}>
      {children}
    </FortressContext.Provider>
  );
};

/**
 * Hook to use Fortress context
 */
export const useFortress = () => {
  const context = useContext(FortressContext);
  if (!context) {
    throw new Error('useFortress must be used within a FortressProvider');
  }
  return context;
};

/**
 * HOC to require feature access
 * Wraps a component and validates access before rendering
 */
export const withFeatureAccess = (Component, featureKey, FallbackComponent = null) => {
  return function FeatureGatedComponent(props) {
    const { isFeatureEnabled, validateAccess, isLoaded } = useFortress();
    const [accessGranted, setAccessGranted] = useState(null);
    const [accessResult, setAccessResult] = useState(null);
    
    useEffect(() => {
      if (!isLoaded) return;
      
      // First check kill-switch
      if (!isFeatureEnabled(featureKey)) {
        setAccessGranted(false);
        setAccessResult({ reason: 'Feature temporarily disabled' });
        return;
      }
      
      // Then validate monetization/access
      validateAccess(featureKey).then(result => {
        setAccessGranted(result.allowed);
        setAccessResult(result);
      });
    }, [isLoaded, isFeatureEnabled, validateAccess]);
    
    if (!isLoaded || accessGranted === null) {
      return null; // Loading
    }
    
    if (!accessGranted && FallbackComponent) {
      return <FallbackComponent accessResult={accessResult} />;
    }
    
    if (!accessGranted) {
      return null;
    }
    
    return <Component {...props} accessResult={accessResult} />;
  };
};

/**
 * Component to conditionally render based on feature access
 */
export const FeatureGate = ({ feature, children, fallback = null, onBlocked = null }) => {
  const { isFeatureEnabled, validateAccess, isLoaded, maintenanceMode } = useFortress();
  const [accessGranted, setAccessGranted] = useState(null);
  const [accessResult, setAccessResult] = useState(null);
  
  useEffect(() => {
    if (!isLoaded) return;
    
    // First check kill-switch
    if (!isFeatureEnabled(feature)) {
      setAccessGranted(false);
      setAccessResult({ reason: 'Feature temporarily disabled' });
      onBlocked?.({ reason: 'Feature temporarily disabled' });
      return;
    }
    
    // Then validate monetization/access
    validateAccess(feature).then(result => {
      setAccessGranted(result.allowed);
      setAccessResult(result);
      if (!result.allowed) {
        onBlocked?.(result);
      }
    });
  }, [isLoaded, feature, isFeatureEnabled, validateAccess, onBlocked]);
  
  // Loading
  if (!isLoaded || accessGranted === null) {
    return null;
  }
  
  // Maintenance mode
  if (maintenanceMode) {
    return fallback || null;
  }
  
  // Access denied
  if (!accessGranted) {
    return fallback || null;
  }
  
  // Access granted - render children
  return typeof children === 'function' 
    ? children(accessResult) 
    : children;
};

export default FortressContext;
